import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Plus, Edit2, Trash2, Save, X, 
  FileText, Zap, Loader2, LogIn, Users, Shield, ShieldCheck, RefreshCw, Sparkles, Play, AlertCircle, School, Inbox, CheckCircle, XCircle, Settings, Code, Lightbulb, BookOpen, UserCheck, Ban, Blocks, Layers, Gift, Flag
} from "lucide-react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import MonthDayPicker from "../components/MonthDayPicker";
import { UserManagementTab, NicknamesTab, QuestionsTab, SubmissionsTab, SchoolsTab, DrillsTab, SolutionCheckTool, ContentTab, VariationsDialog } from "../components/admin";
import { readQuestionsTrack } from "../components/admin/QuestionsTab";
import { BlocksInputsEditor } from "../components/admin/BlocksInputsEditor";
import { QuestionOrderTab } from "../components/admin/QuestionOrderTab";
import { ReferralsTab } from "../components/admin/ReferralsTab";
import { BlockRulesEditor } from "../components/blocks/BlockRulesEditor";
import { BLOCKS_CATEGORIES } from "../components/blocks/blocksSetup";
import { NavGridEditor, navProblems } from "../components/nav/NavGridEditor";
import { NavRulesEditor } from "../components/nav/NavRulesEditor";
import { NavGrid } from "../components/nav/NavGrid";
import { emptyNav } from "../components/nav/navShared";
import { MODES, MODE_META, isBlockMode, normalizeMode } from "../lib/modes";
import { GenerateBlocksTool } from "../components/admin/GenerateBlocksTool";
import { DrillRepairTool } from "../components/admin/DrillRepairTool";
import { GemFillTool } from "../components/admin/GemFillTool";
import { StripeWebhookHealth } from "../components/admin/StripeWebhookHealth";
import { BlockSolutionPanel } from "../components/admin/BlockSolutionPanel";
import { PythonSampleEditor } from "../components/admin/PythonSampleEditor";
import { DeleteQuestionDialog } from "../components/admin/DeleteQuestionDialog";
import { toastDrillReport } from "../components/admin/DrillUsageNotice";
import { convertAndVerify } from "../components/blocks/solutionTools";
const BlocksSolutionView = lazy(() => import("../components/blocks/BlocksSolutionView").then((m) => ({ default: m.BlocksSolutionView })));

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AdminDashboard({ user: propUser, setUser: propSetUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUserState] = useState(location.state?.user || propUser || null);
  const [loading, setLoading] = useState(!location.state?.user && !propUser);
  const [questions, setQuestions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  // Nickname lists state
  const [nicknameLists, setNicknameLists] = useState({
    adjectives: [],
    colours: [],
    creatures: []
  });
  
  // Question submissions and school applications state
  const [questionSubmissions, setQuestionSubmissions] = useState([]);
  const [schoolApplications, setSchoolApplications] = useState([]);
  const [reviewingSubmission, setReviewingSubmission] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [editSubmissionData, setEditSubmissionData] = useState(null);
  const [editTestCases, setEditTestCases] = useState([]);
  const [savingSubmission, setSavingSubmission] = useState(false);
  const [validatingSubmission, setValidatingSubmission] = useState(false);
  const [rejectingSubmission, setRejectingSubmission] = useState(false);  // reveals the optional reason box
  const [rejectReason, setRejectReason] = useState("");
  const [submissionValidationResult, setSubmissionValidationResult] = useState(null);
  
  // Tools state
  const [generatingSampleCodes, setGeneratingSampleCodes] = useState(false);
  const [sampleCodeResult, setSampleCodeResult] = useState(null);
  const [generatingHints, setGeneratingHints] = useState(false);
  const [hintsResult, setHintsResult] = useState(null);
  const [creatingTestUsers, setCreatingTestUsers] = useState(false);
  const [testUsersResult, setTestUsersResult] = useState(null);
  const [showTestUsersConfirm, setShowTestUsersConfirm] = useState(false);
  const [testUsersOpts, setTestUsersOpts] = useState({ modes: ["python"], min_points: 10, max_points: 100 });
  const [scanningProfanity, setScanningProfanity] = useState(false);
  const [showGenerateBlocks, setShowGenerateBlocks] = useState(false);
  const [questionsTrack, setQuestionsTrack] = useState(readQuestionsTrack);
  const [convertingSolutions, setConvertingSolutions] = useState(new Set());
  const [generatingNavSolution, setGeneratingNavSolution] = useState(false);
  const [variationsFor, setVariationsFor] = useState(null);  // question being multiplied into 5 similar problems
  const [showSolutionPreview, setShowSolutionPreview] = useState(false);
  const [profanityResult, setProfanityResult] = useState(null);
  const [scanPhase, setScanPhase] = useState(null);
  // Editable profanity word lists (super admin)
  const [profanityWords, setProfanityWords] = useState({ custom_words: "", allowlist: "" });
  const [loadingProfanityWords, setLoadingProfanityWords] = useState(false);
  const [savingProfanityWords, setSavingProfanityWords] = useState(false);
  const [updatingLegacyCreators, setUpdatingLegacyCreators] = useState(false);
  const [legacyCreatorsResult, setLegacyCreatorsResult] = useState(null);
  const [resettingDrills, setResettingDrills] = useState(false);
  const [drillResetResult, setDrillResetResult] = useState(null);
  const [showDrillResetConfirm, setShowDrillResetConfirm] = useState(false);

  // Sync user state with parent
  const setUser = (userData) => {
    setUserState(userData);
    if (propSetUser) {
      propSetUser(userData);
    }
  };

  const isMasterAdmin = user?.role === "master_admin";
  const isAdminOrMaster = user?.role === "admin" || user?.role === "master_admin";

  useEffect(() => {
    if (propUser) {
      setUserState(propUser);
      if (propUser.is_admin) {
        fetchData();
      }
      setLoading(false);
    } else if (!location.state?.user) {
      checkAuth();
    } else {
      fetchData();
    }
  }, [location.state, propUser]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        setLoading(false);
        return;
      }
      
      const userData = await response.json();
      if (!userData.is_admin && userData.role !== "master_admin" && userData.role !== "admin") {
        setLoading(false);
        return;
      }
      
      setUser(userData);
      fetchData();
    } catch (error) {
      console.error("Auth check error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const questionsRes = await fetch(`${API}/admin/questions?per_page=10000`, { credentials: "include" });
      
      if (questionsRes.ok) {
        const data = await questionsRes.json();
        // Handle both paginated response and legacy array response
        setQuestions(data.questions || data);
      }
      
      // Fetch users and nickname lists if master admin
      if (user?.role === "master_admin" || propUser?.role === "master_admin") {
        fetchUsers();
        fetchNicknameLists();
        fetchProfanityWords();
      }
      
      // Fetch question submissions and school applications for admins
      fetchQuestionSubmissions();
      fetchSchoolApplications();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchQuestionSubmissions = async () => {
    try {
      const response = await fetch(`${API}/admin/question-submissions`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setQuestionSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error("Error fetching question submissions:", error);
    }
  };

  const fetchSchoolApplications = async () => {
    try {
      const response = await fetch(`${API}/admin/school-applications`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSchoolApplications(data.applications || []);
      }
    } catch (error) {
      console.error("Error fetching school applications:", error);
    }
  };

  const handleApproveSubmission = async (questionId) => {
    try {
      const response = await fetch(`${API}/admin/question-submissions/${questionId}/approve`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Question approved and added to challenge pool!");
        fetchQuestionSubmissions();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to approve question");
      }
    } catch (error) {
      toast.error("Failed to approve question");
    }
  };

  const handleSuspendSubmitter = async (questionId, submitterName) => {
    if (!window.confirm(`Suspend ${submitterName || "this user"} from submitting for 1 week?`)) return;
    try {
      const response = await fetch(`${API}/admin/question-submissions/${questionId}/suspend-submitter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ days: 7 })
      });
      const data = await response.json();
      if (response.ok) {
        const until = data.suspended_until ? new Date(data.suspended_until).toLocaleDateString() : "";
        toast.success(`${data.user_name || submitterName} suspended from submitting until ${until}`);
      } else {
        toast.error(data.detail || "Failed to suspend user");
      }
    } catch (error) {
      toast.error("Failed to suspend user");
    }
  };

  const handleRejectSubmission = async (questionId) => {
    try {
      const response = await fetch(`${API}/admin/question-submissions/${questionId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: rejectReason.trim() || null }),
      });
      if (response.ok) {
        toast.success("Question rejected - the creator has been notified");
        fetchQuestionSubmissions();
        setEditingSubmission(null);
        setRejectingSubmission(false);
        setRejectReason("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reject question");
      }
    } catch (error) {
      toast.error("Failed to reject question");
    }
  };

  const openSubmissionEditor = async (submission) => {
    try {
      // Fetch full submission details including test cases
      const response = await fetch(`${API}/admin/question-submissions/${submission.question_id}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        const fullSubmission = data.submission;
        setEditingSubmission(fullSubmission);
        setEditSubmissionData({
          title: fullSubmission.title || "",
          description: fullSubmission.description || "",
          input_format: fullSubmission.input_format || "",
          output_format: fullSubmission.output_format || "",
          example_input: fullSubmission.example_input || "",
          example_output: fullSubmission.example_output || "",
          sample_solution: fullSubmission.sample_solution || "",
          hint: fullSubmission.hint || ""
        });
        setEditTestCases(fullSubmission.test_cases || [{ input: "", expected_output: "" }, { input: "", expected_output: "" }]);
        setSubmissionValidationResult(null);
      } else {
        toast.error("Failed to load submission details");
      }
    } catch (error) {
      toast.error("Failed to load submission details");
    }
  };

  const handleEditSubmissionChange = (field, value) => {
    setEditSubmissionData(prev => ({ ...prev, [field]: value }));
    setSubmissionValidationResult(null);
  };

  const handleEditTestCaseChange = (index, field, value) => {
    setEditTestCases(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setSubmissionValidationResult(null);
  };

  const addEditTestCase = () => {
    setEditTestCases(prev => [...prev, { input: "", expected_output: "" }]);
  };

  const removeEditTestCase = (index) => {
    if (editTestCases.length <= 2) {
      toast.error("At least 2 test cases required");
      return;
    }
    setEditTestCases(prev => prev.filter((_, i) => i !== index));
  };

  const validateSubmissionSolution = async () => {
    if (editingSubmission?.mode === "junior" || editingSubmission?.blocks?.kind === "nav") {
      setValidatingSubmission(true);
      setSubmissionValidationResult(null);
      try {
        const response = await fetch(`${API}/code/nav-run`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            nav: editingSubmission.blocks.nav,
            code: editingSubmission.blocks.solution_python || editSubmissionData.sample_solution,
            workspace: editingSubmission.blocks.solution_workspace || null,
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail?.message || data.detail || "Run failed");
        const passed = !!data.reached;
        setSubmissionValidationResult({ results: [{ passed, expected: "treasure reached", actual: data.message || "", error: data.error }], allPassed: passed, nav: true });
        passed ? toast.success("The drill program reaches the treasure") : toast.error(data.message || "The drill didn't reach the treasure");
      } catch (error) {
        toast.error(error.message || "Failed to validate drill program");
      } finally {
        setValidatingSubmission(false);
      }
      return;
    }
    if (!editSubmissionData.sample_solution.trim()) {
      toast.error("Please provide a sample solution");
      return;
    }

    const validTestCases = editTestCases.filter(tc => tc.input.trim() || tc.expected_output.trim());
    if (validTestCases.length < 2) {
      toast.error("Please provide at least 2 test cases");
      return;
    }

    setValidatingSubmission(true);
    setSubmissionValidationResult(null);

    try {
      const results = [];
      for (const tc of validTestCases) {
        const response = await fetch(`${API}/code/test`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            code: editSubmissionData.sample_solution,
            custom_input: tc.input
          })
        });

        const data = await response.json();
        const actualOutput = data.output?.trim() || "";
        const expectedOutput = tc.expected_output.trim();
        const passed = data.success && actualOutput === expectedOutput;
        
        results.push({
          input: tc.input,
          expected: expectedOutput,
          actual: actualOutput,
          passed,
          error: data.error
        });
      }

      const allPassed = results.every(r => r.passed);
      setSubmissionValidationResult({ results, allPassed });

      if (allPassed) {
        toast.success("All test cases passed!");
      } else {
        toast.error("Some test cases failed");
      }
    } catch (error) {
      toast.error("Failed to validate solution");
    } finally {
      setValidatingSubmission(false);
    }
  };

  const saveAndApproveSubmission = async () => {
    if (!submissionValidationResult?.allPassed) {
      toast.error("Please validate the solution first");
      return;
    }

    setSavingSubmission(true);
    try {
      // First update the submission
      const updateResponse = await fetch(`${API}/admin/question-submissions/${editingSubmission.question_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...editSubmissionData,
          test_cases: editTestCases.filter(tc => tc.input.trim() || tc.expected_output.trim())
        })
      });

      if (!updateResponse.ok) {
        const error = await updateResponse.json();
        toast.error(error.detail || "Failed to update submission");
        setSavingSubmission(false);
        return;
      }

      // Then approve it
      const approveResponse = await fetch(`${API}/admin/question-submissions/${editingSubmission.question_id}/approve`, {
        method: "POST",
        credentials: "include"
      });

      if (approveResponse.ok) {
        toast.success("Question updated and approved!");
        setEditingSubmission(null);
        fetchQuestionSubmissions();
        fetchData();
      } else {
        const error = await approveResponse.json();
        toast.error(error.detail || "Failed to approve question");
      }
    } catch (error) {
      toast.error("Failed to save and approve");
    } finally {
      setSavingSubmission(false);
    }
  };

  const handleApproveSchool = async (applicationId) => {
    try {
      const response = await fetch(`${API}/admin/school-applications/${applicationId}/approve`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("School approved!");
        fetchSchoolApplications();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to approve school");
      }
    } catch (error) {
      toast.error("Failed to approve school");
    }
  };

  const handleRejectSchool = async (applicationId) => {
    try {
      const response = await fetch(`${API}/admin/school-applications/${applicationId}/reject`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("School application rejected");
        fetchSchoolApplications();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reject school");
      }
    } catch (error) {
      toast.error("Failed to reject school");
    }
  };

  const fetchNicknameLists = async () => {
    try {
      const response = await fetch(`${API}/admin/nickname-lists`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setNicknameLists(data);
      }
    } catch (error) {
      console.error("Error fetching nickname lists:", error);
    }
  };

  const handleGenerateSampleCodes = async () => {
    if (!window.confirm("This will generate sample Python code for 3 questions at a time. The code will be validated against test cases. Continue?")) {
      return;
    }
    
    setGeneratingSampleCodes(true);
    setSampleCodeResult(null);
    
    try {
      const response = await fetch(`${API}/admin/generate-sample-codes`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const result = await response.json();
        setSampleCodeResult(result);
        if (result.failed > 0) {
          toast.warning(`Generated ${result.generated} sample codes, ${result.failed} failed`);
        } else {
          toast.success(`Generated ${result.generated} sample codes!`);
        }
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate sample codes");
      }
    } catch (error) {
      console.error("Error generating sample codes:", error);
      toast.error(`Error generating sample codes: ${error.message || "Network error"}`);
    } finally {
      setGeneratingSampleCodes(false);
    }
  };

  const [recheckingSolutions, setRecheckingSolutions] = useState(new Set());
  // Re-check one question's sample solution against its tests; `quiet` only reports problems (used right after saving)
  const handleRecheckSolution = async (question, { quiet = false } = {}) => {
    setRecheckingSolutions((prev) => new Set(prev).add(question.question_id));
    try {
      const res = await fetch(`${API}/admin/questions/${question.question_id}/solution-check`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (!quiet) toast.error(data.detail || "Could not check this solution");
        return;
      }
      setQuestions((prev) => prev.map((q) => (q.question_id === question.question_id ? { ...q, solution_check: data.solution_check } : q)));
      const c = data.solution_check;
      const name = question.title ? `"${question.title}"` : "This question";
      if (c.status === "pass" && c.blocks_status !== "fail") { if (!quiet) toast.success(`${name} passes all ${c.total} test(s)`); }
      else if (c.status === "fail") toast.error(`${name} fails ${c.total - c.passed} of ${c.total} test(s)`, { description: quiet ? "Saved, but the sample solution doesn't pass its own tests." : undefined });
      else if (c.blocks_status === "fail") toast.error(`${name}: the block solution fails the tests`);
      else toast.warning(`${name}: ${c.status === "no_tests" ? "no test data to check against" : "no sample solution to check"}`);
    } catch {
      if (!quiet) toast.error("Could not check this solution");
    } finally {
      setRecheckingSolutions((prev) => { const n = new Set(prev); n.delete(question.question_id); return n; });
    }
  };

  const handleRunProfanityScan = async () => {
    setScanningProfanity(true);
    setProfanityResult(null);
    setScanPhase(null);
    try {
      // Start (or attach to) a background scan job
      const startRes = await fetch(`${API}/admin/profanity-scan`, {
        method: "POST",
        credentials: "include",
      });
      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({}));
        toast.error(err.detail || "Could not start scan");
        setScanningProfanity(false);
        return;
      }

      // Poll for completion
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 10 * 60 * 1000) {
          toast.error("Scan is taking too long. Please try again later.");
          setScanningProfanity(false);
          return;
        }
        try {
          const res = await fetch(`${API}/admin/profanity-scan`, { credentials: "include" });
          const data = await res.json();
          if (!res.ok) {
            toast.error(data.detail || "Scan status check failed");
            setScanningProfanity(false);
            return;
          }
          if (data.status === "failed") {
            toast.error(data.error || "Scan failed");
            setScanningProfanity(false);
            return;
          }
          if (data.status === "done") {
            setProfanityResult(data);
            setScanningProfanity(false);
            if ((data.total_flagged || 0) === 0) {
              toast.success("Scan complete — no offensive words found!");
            } else {
              toast.warning(`Scan complete — ${data.total_flagged} item(s) flagged`);
            }
            return;
          }
          // running / idle / unknown -> keep polling
          if (data.status === "running") setScanPhase(data);
          setTimeout(poll, 2000);
        } catch (e) {
          toast.error("Scan status check failed");
          setScanningProfanity(false);
        }
      };
      setTimeout(poll, 1500);
    } catch (error) {
      toast.error("Could not start scan");
      setScanningProfanity(false);
    }
  };

  const fetchProfanityWords = async () => {
    setLoadingProfanityWords(true);
    try {
      const res = await fetch(`${API}/admin/profanity-words`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setProfanityWords({
          custom_words: (data.custom_words || []).join(", "),
          allowlist: (data.allowlist || []).join(", "),
        });
      }
    } catch (e) {
      /* ignore */
    } finally {
      setLoadingProfanityWords(false);
    }
  };

  const parseWordList = (text) =>
    (text || "")
      .split(/[\s,]+/)
      .map((w) => w.trim().toLowerCase())
      .filter(Boolean);

  const handleSaveProfanityWords = async () => {
    setSavingProfanityWords(true);
    try {
      const res = await fetch(`${API}/admin/profanity-words`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          custom_words: parseWordList(profanityWords.custom_words),
          allowlist: parseWordList(profanityWords.allowlist),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setProfanityWords({
          custom_words: (data.custom_words || []).join(", "),
          allowlist: (data.allowlist || []).join(", "),
        });
        toast.success("Profanity word lists updated — applied across the site.");
      } else {
        toast.error(data.detail || "Could not update word lists");
      }
    } catch (e) {
      toast.error("Could not update word lists");
    } finally {
      setSavingProfanityWords(false);
    }
  };

  const handleGenerateHints = async () => {
    if (!window.confirm("This will generate AI hints for questions with missing or default W3Schools hints (5 at a time). Continue?")) {
      return;
    }
    
    setGeneratingHints(true);
    setHintsResult(null);
    
    try {
      const response = await fetch(`${API}/admin/generate-hints`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const result = await response.json();
        setHintsResult(result);
        toast.success(`Generated ${result.generated} hints! (${result.failed} failed)`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate hints");
      }
    } catch (error) {
      console.error("Error generating hints:", error);
      toast.error("Error generating hints");
    } finally {
      setGeneratingHints(false);
    }
  };

  const handleCreateTestUsers = async () => {
    setShowTestUsersConfirm(false);
    setCreatingTestUsers(true);
    setTestUsersResult(null);
    
    try {
      const response = await fetch(`${API}/admin/create-test-users`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...testUsersOpts, count: 10 }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setTestUsersResult(result);
        toast.success(`Created ${result.total_created} test users on ${result.modes.map((m) => MODE_META[m]?.label || m).join(", ")}!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create test users");
      }
    } catch (error) {
      console.error("Error creating test users:", error);
      toast.error("Error creating test users");
    } finally {
      setCreatingTestUsers(false);
    }
  };

  const handleUpdateLegacyCreators = async () => {
    setUpdatingLegacyCreators(true);
    setLegacyCreatorsResult(null);
    
    try {
      const response = await fetch(`${API}/admin/update-legacy-creators`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const result = await response.json();
        setLegacyCreatorsResult(result);
        if (result.updated_count > 0) {
          toast.success(`Updated ${result.updated_count} questions with creator!`);
        } else {
          toast.info("All questions already have creators assigned.");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update legacy creators");
      }
    } catch (error) {
      console.error("Error updating legacy creators:", error);
      toast.error("Error updating legacy creators");
    } finally {
      setUpdatingLegacyCreators(false);
    }
  };

  const handleResetAllDrills = async () => {
    setShowDrillResetConfirm(false);
    setResettingDrills(true);
    setDrillResetResult(null);
    
    try {
      const response = await fetch(`${API}/admin/reset-all-drills`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const result = await response.json();
        setDrillResetResult(result);
        toast.success(`Reset complete! ${result.assignments_deleted} assignments and ${result.progress_deleted} progress records deleted.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reset drills");
      }
    } catch (error) {
      console.error("Error resetting drills:", error);
      toast.error("Error resetting drills");
    } finally {
      setResettingDrills(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        // Handle both paginated response and legacy array response
        setAllUsers(data.users || data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        toast.success(`User role updated to ${newRole}`);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update role");
      }
    } catch (error) {
      toast.error("Error updating user role");
    }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSaveQuestion = () => saveQuestion();

  // Saves the question; resolves to the saved question_id (null on failure). `stay` keeps the dialog open
  // so follow-up actions (generate / edit the block solution) work on the state the admin is looking at.
  const saveQuestion = async ({ stay = false } = {}) => {
    // Reset validation error
    setValidationError(null);
    
    // Validate that we have test cases and sample solution
    const testCases = editingQuestion.test_cases || [];
    const sampleSolution = editingQuestion.sample_solution || "";
    const isNavQuestion = editingQuestion.mode === "junior";
    const isBlocksQuestion = editingQuestion.mode === "blocks";   // graded by tests, solved with blocks - Python sample optional
    
    if (isNavQuestion) {
      if (!(editingQuestion.title || "").trim()) {
        toast.error("Please add a title");
        return null;
      }
      const problems = navProblems(editingQuestion.blocks?.nav);
      if (problems.length) {
        toast.error(problems[0]);
        return null;
      }
    } else if (testCases.length === 0) {
      toast.error("Please add at least one test case");
      return null;
    } else if (!sampleSolution.trim() && !isBlocksQuestion) {
      toast.error("Please provide a sample solution");
      return null;
    }
    if (isBlocksQuestion && !(editingQuestion.title || "").trim()) {
      toast.error("Please add a title");
      return null;
    }
    const skipPythonCheck = isNavQuestion || (isBlocksQuestion && !sampleSolution.trim());
    
    // Check the sample solution against the test cases - a failing sample is a WARNING, not a blocker:
    // the question still saves (flagged by the health check) so the admin can fix it later
    let sampleFailed = false, checkError = null;
    if (!skipPythonCheck) {
      setValidating(true);
      try {
        const testCasesForValidation = testCases.map(tc => ({
          input: (tc.inputs || []).join('\n'),
          expected_output: tc.expected_output || ''
        }));
        const validateResponse = await fetch(`${API}/admin/validate-solution`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            code: sampleSolution,
            test_cases: testCasesForValidation,
            mode: isBlocksQuestion ? "blocks" : null
          })
        });
        const validateResult = await validateResponse.json();
        if (!validateResponse.ok || !validateResult.valid) {
          setValidationError(validateResult.details || validateResult.detail || "Sample solution failed validation");
          sampleFailed = true;
        }
      } catch (error) {
        checkError = `Saved, but the sample solution couldn't be checked: ${error.message}`;
      }
      setValidating(false);
    }
    
    // Now save the question
    setSaving(true);
    let savedId = null;
    try {
      const isNew = !editingQuestion.question_id;
      const url = isNew 
        ? `${API}/admin/questions`
        : `${API}/admin/questions/${editingQuestion.question_id}`;
      
      // Convert inputs array to input string (joined by newlines)
      const testCasesForBackend = (editingQuestion.test_cases || []).map(tc => ({
        input: (tc.inputs || []).join('\n'),
        expected_output: tc.expected_output || ''
      }));
      
      // Use date directly (MM-DD format)
      const dateValue = editingQuestion.date || null;
      
      const response = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: editingQuestion.title,
          description: isNavQuestion ? "" : editingQuestion.description,
          input_format: editingQuestion.input_format,
          output_format: editingQuestion.output_format,
          example_input: editingQuestion.example_input,
          example_output: editingQuestion.example_output,
          sample_solution: editingQuestion.sample_solution || "",
          hint: editingQuestion.hint || "",
          test_cases: testCasesForBackend,
          pool: editingQuestion.pool,
          date: dateValue,
          mode: normalizeMode(editingQuestion.mode),
          blocks: isBlockMode(editingQuestion.mode) ? { ...(editingQuestion.blocks || {}), kind: isNavQuestion ? "nav" : (editingQuestion.blocks?.kind || "program"), inputs: editingQuestion.blocks?.inputs || [] } : null,
          created_by_email: editingQuestion.created_by_email || null,
        }),
      });

      if (response.ok) {
        const saved = await response.json().catch(() => ({}));
        savedId = saved.question_id || editingQuestion.question_id;
        const savedTitle = editingQuestion.title;
        // A failing sample still saves; the health re-check below toasts "fails N of M tests" and flags it in the list
        if (checkError) toast.warning(checkError, { duration: 8000 });
        else if (!sampleFailed) toast.success(isNew ? "Question created!" : stay ? "Question saved" : "Question updated!");
        if (stay) {
          if (isNew && savedId) setEditingQuestion((prev) => ({ ...prev, question_id: savedId }));
          await fetchData();
          if (sampleFailed && savedId) handleRecheckSolution({ question_id: savedId, title: savedTitle }, { quiet: true });
        } else {
          setIsDialogOpen(false);
          setEditingQuestion(null);
          await fetchData();
          if (savedId) handleRecheckSolution({ question_id: savedId, title: savedTitle }, { quiet: true });
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to save question");
      }
    } catch (error) {
      toast.error("Error saving question");
    } finally {
      setSaving(false);
    }
    return savedId;
  };

  // Junior Blocks: auto-solve the grid on the server (saves the current state first so it solves what's on screen)
  const handleGenerateNavSolution = async () => {
    const id = await saveQuestion({ stay: true });
    if (!id) return;
    setGeneratingNavSolution(true);
    try {
      const res = await fetch(`${API}/admin/questions/${id}/blocks-solution/nav-auto`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail?.message || data.detail || "Could not generate a drill program for this grid");
        return;
      }
      toast.success(data.message || "Drill program generated");
      if (data.question) {
        setEditingQuestion((prev) => (prev ? { ...prev, blocks: data.question.blocks, sample_solution: data.question.sample_solution } : prev));
        setQuestions((prev) => prev.map((q) => (q.question_id === id ? { ...q, ...data.question } : q)));
        setShowSolutionPreview(true);
      }
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setGeneratingNavSolution(false);
    }
  };

  // "Generate 5 similar problems" from the edit dialog: save what's on screen first so the variants copy it
  const handleGenerateVariationsFromDialog = async () => {
    const id = await saveQuestion({ stay: true });
    if (!id) return;
    const fresh = questions.find((q) => q.question_id === id) || editingQuestion;
    setVariationsFor({ ...fresh, ...editingQuestion, question_id: id });
  };

  const handleVariationsCreated = useCallback(() => { fetchData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Block tracks: "Build in blocks" must not lose unsaved changes (grid, inputs, test cases) - save, then open the block editor
  const handleOpenBlocksEditor = async () => {
    const id = await saveQuestion({ stay: true });
    if (!id) return;
    setIsDialogOpen(false);
    navigate(`/admin/blocks-solution/${id}`);
  };

  // Blocks: auto-convert the Python sample into blocks; saves the dialog state first so the conversion uses it
  const handleConvertFromDialog = async () => {
    const id = await saveQuestion({ stay: true });
    if (!id) return;
    await handleConvertSolution({ ...editingQuestion, question_id: id });
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const handleDeleteQuestion = (questionId) => {
    const q = questions.find((x) => x.question_id === questionId);
    setDeleteTarget({ question_id: questionId, title: q?.title || "" });
  };

  const deleteQuestionNow = async (questionId) => {
    try {
      const response = await fetch(`${API}/admin/questions/${questionId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.success("Question deleted!");
        toastDrillReport(data.drills);
        fetchData();
      } else {
        toast.error("Failed to delete question");
      }
    } catch (error) {
      toast.error("Error deleting question");
    }
  };

  // Super admin bulk actions from the Questions tab (delete / move between pools). Resolves true when the server accepted the request.
  const handleBulkQuestions = async (action, questionIds, pool) => {
    try {
      const res = await fetch(`${API}/admin/questions/bulk`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_ids: questionIds, action, pool }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail?.message || data.detail || "Bulk action failed");
        return false;
      }
      const n = (data.done || []).length;
      const skipped = data.skipped || [];
      if (action === "delete") { toast.success(`Deleted ${n} question${n === 1 ? "" : "s"}`); toastDrillReport(data.drills); }
      else toast.success(`Moved ${n} question${n === 1 ? "" : "s"} to the ${data.pool === "daily" ? "Warm Up" : "Challenge"} Pool`);
      if (skipped.length) toast.warning(`Skipped ${skipped.length}: ${skipped.map((s) => `${s.title} (${s.reason})`).join("; ")}`, { duration: 8000 });
      fetchData();
      return true;
    } catch (error) {
      toast.error("Bulk action failed");
      return false;
    }
  };

  const handleDuplicateQuestion = async (question) => {
    try {
      // Convert inputs array to input string (joined by newlines)
      const testCasesForBackend = (question.test_cases || []).map(tc => ({
        input: tc.input || (tc.inputs || []).join('\n'),
        expected_output: tc.expected_output || ''
      }));

      const response = await fetch(`${API}/admin/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: `${question.title} (Copy)`,
          description: question.description,
          input_format: question.input_format || "",
          output_format: question.output_format || "",
          example_input: question.example_input || "",
          example_output: question.example_output || "",
          sample_solution: question.sample_solution || question.starter_code || "",
          test_cases: testCasesForBackend,
          pool: question.pool,
          date: null, // Reset date for duplicated question
          mode: normalizeMode(question.mode),
          blocks: question.mode === "junior" ? { kind: "nav", inputs: [], nav: question.blocks?.nav } : question.mode === "blocks" ? { inputs: question.blocks?.inputs || [], starter_workspace: question.blocks?.starter_workspace || null } : null,
        }),
      });

      if (response.ok) {
        toast.success("Question duplicated!");
        fetchData();
      } else {
        toast.error("Failed to duplicate question");
      }
    } catch (error) {
      toast.error("Error duplicating question");
    }
  };

  // Copy a Python question into the Blocks track (inputs inferred server-side), then open it for review
  const handleMakeBlocksVersion = async (question) => {
    try {
      const response = await fetch(`${API}/admin/questions/${question.question_id}/make-blocks-version`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.detail || "Failed to create Blocks version");
        return;
      }
      toast.success("Blocks version created - check the detected inputs");
      localStorage.setItem("admin_questions_track", "blocks");
      setQuestionsTrack("blocks");
      const summary = await convertSolutions([data.question], { silent: true });
      openEditQuestion(summary.updated[data.question.question_id] || data.question);
    } catch (error) {
      toast.error("Error creating Blocks version");
    }
  };

  // Python sample solution -> blocks -> Python -> verified against the tests -> stored
  const convertSolutions = async (targets, { silent = false } = {}) => {
    const ids = new Set(targets.map((q) => q.question_id));
    setConvertingSolutions((prev) => new Set([...prev, ...ids]));
    const summary = { verified: 0, failed: [], updated: {} };
    for (const q of targets) {
      try {
        const res = await convertAndVerify(q);
        if (res.status === "verified") { summary.verified++; summary.updated[q.question_id] = res.question; }
        else summary.failed.push({ title: q.title, reason: res.reason });
      } catch (e) {
        summary.failed.push({ title: q.title, reason: e.message });
      }
    }
    setConvertingSolutions((prev) => { const n = new Set(prev); ids.forEach((id) => n.delete(id)); return n; });
    if (!silent) {
      if (targets.length === 1) {
        summary.verified ? toast.success("Block solution created and verified") : toast.error(summary.failed[0]?.reason || "Could not convert");
      } else {
        toast[summary.failed.length ? "warning" : "success"](`${summary.verified} block solution${summary.verified === 1 ? "" : "s"} verified${summary.failed.length ? `, ${summary.failed.length} need a hand-built solution` : ""}`);
      }
    }
    await fetchData();
    return summary;
  };

  const handleConvertSolution = async (question) => {
    const summary = await convertSolutions([question]);
    const updated = summary.updated[question.question_id];
    if (updated) {
      setEditingQuestion((prev) => (prev && prev.question_id === question.question_id ? { ...prev, blocks: updated.blocks, sample_solution: updated.sample_solution || prev.sample_solution } : prev));
    }
  };

  const openNewQuestion = (track = "python") => {
    setEditingQuestion({
      title: "",
      description: "",
      input_format: "",
      output_format: "",
      example_input: "",
      example_output: "",
      sample_solution: "",
      hint: "",
      test_cases: [{ inputs: [""], expected_output: "" }],
      pool: "daily",
      date: null,
      created_by_email: "",
      mode: normalizeMode(track),
      blocks: track === "junior" ? { kind: "nav", inputs: [], nav: emptyNav() } : track === "blocks" ? { inputs: [] } : null,
    });
    setIsDialogOpen(true);
  };

  const openEditQuestion = (question) => {
    // Convert old format (single input string) to new format (array of inputs)
    const convertedQuestion = { ...question };
    if (convertedQuestion.test_cases) {
      convertedQuestion.test_cases = convertedQuestion.test_cases.map(tc => {
        if (tc.inputs) {
          return tc; // Already in new format
        }
        // Convert old format: split input by newlines into array
        const inputLines = tc.input ? tc.input.split('\n') : [''];
        return {
          inputs: inputLines,
          expected_output: tc.expected_output || ''
        };
      });
    }
    // Ensure created_by_email is available for editing
    if (!convertedQuestion.created_by_email) {
      convertedQuestion.created_by_email = "";
    }
    // Older seeded questions keep their solution in the legacy starter_code field
    if (!convertedQuestion.sample_solution && convertedQuestion.starter_code) {
      convertedQuestion.sample_solution = convertedQuestion.starter_code;
    }
    setValidationError(null); // Clear any previous validation errors
    setShowSolutionPreview(false);
    setEditingQuestion(convertedQuestion);
    setIsDialogOpen(true);
  };

  const handleTestSolution = async () => {
    const testCases = editingQuestion?.test_cases || [];
    const sampleSolution = editingQuestion?.sample_solution || "";
    
    if (testCases.length === 0) {
      toast.error("Please add at least one test case");
      return;
    }
    
    if (!sampleSolution.trim()) {
      toast.error("Please enter a sample solution");
      return;
    }
    
    setValidating(true);
    setValidationError(null);
    
    try {
      const testCasesForValidation = testCases.map(tc => ({
        input: (tc.inputs || []).join('\n'),
        expected_output: tc.expected_output || ''
      }));
      
      const response = await fetch(`${API}/admin/validate-solution`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          code: sampleSolution,
          test_cases: testCasesForValidation,
          mode: editingQuestion?.mode === "blocks" ? "blocks" : null
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.valid) {
        toast.success(`All ${result.passed}/${result.total} tests passed!`);
      } else {
        setValidationError(result.details || result.detail || "Solution failed tests");
        toast.error("Solution doesn't pass all tests");
      }
    } catch (error) {
      toast.error("Error testing solution: " + error.message);
    } finally {
      setValidating(false);
    }
  };

  const addTestCase = () => {
    setEditingQuestion((prev) => ({
      ...prev,
      test_cases: [...(prev.test_cases || []), { inputs: [""], expected_output: "" }],
    }));
  };

  const addInputLine = (testCaseIndex) => {
    setEditingQuestion((prev) => ({
      ...prev,
      test_cases: prev.test_cases.map((tc, i) =>
        i === testCaseIndex 
          ? { ...tc, inputs: [...(tc.inputs || ['']), ''] }
          : tc
      ),
    }));
  };

  const removeInputLine = (testCaseIndex, inputIndex) => {
    setEditingQuestion((prev) => ({
      ...prev,
      test_cases: prev.test_cases.map((tc, i) =>
        i === testCaseIndex 
          ? { ...tc, inputs: tc.inputs.filter((_, idx) => idx !== inputIndex) }
          : tc
      ),
    }));
  };

  const updateTestCaseInput = (testCaseIndex, inputIndex, value) => {
    setEditingQuestion((prev) => ({
      ...prev,
      test_cases: prev.test_cases.map((tc, i) =>
        i === testCaseIndex 
          ? { 
              ...tc, 
              inputs: tc.inputs.map((inp, idx) => idx === inputIndex ? value : inp) 
            }
          : tc
      ),
    }));
  };

  const updateTestCase = (index, field, value) => {
    setEditingQuestion((prev) => ({
      ...prev,
      test_cases: prev.test_cases.map((tc, i) =>
        i === index ? { ...tc, [field]: value } : tc
      ),
    }));
  };

  const removeTestCase = (index) => {
    setEditingQuestion((prev) => ({
      ...prev,
      test_cases: prev.test_cases.filter((_, i) => i !== index),
    }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated or not admin
  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="admin-login-prompt">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="p-4 rounded-2xl bg-primary/10 w-fit mx-auto mb-6">
            <LogIn className="w-12 h-12 text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-3">Admin Access Required</h1>
          <p className="text-muted-foreground mb-6">
            Please sign in with your admin account to manage questions.
          </p>
          <Button
            onClick={handleLogin}
            className="gap-2 rounded-xl px-8 h-12"
            data-testid="admin-sign-in-btn"
          >
            Sign in with Google
          </Button>
        </motion.div>
      </div>
    );
  }


  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">
              Admin Dashboard
            </h1>
            {isMasterAdmin && (
              <Badge className="bg-secondary text-secondary-foreground">
                <ShieldCheck className="w-3 h-3 mr-1" />
                Master Admin
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Manage questions{isMasterAdmin ? ", users," : ""} and daily challenges
          </p>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "questions"} className="space-y-6">
          <TabsList className={`flex flex-wrap gap-1 sm:grid sm:w-full sm:max-w-5xl ${isMasterAdmin ? "sm:grid-cols-10" : isAdminOrMaster ? "sm:grid-cols-6" : "sm:grid-cols-4"} h-auto p-1`}>
            <TabsTrigger value="questions" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-questions">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Questions</span>
              <span className="sm:hidden">Qs</span>
            </TabsTrigger>
            {isAdminOrMaster && (
              <TabsTrigger value="drills" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-drills">
                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Drills</span>
                <span className="sm:hidden">Drills</span>
              </TabsTrigger>
            )}
            {isAdminOrMaster && (
              <TabsTrigger value="order" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-order">
                <Flag className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Campaign</span>
                <span className="sm:hidden">Campaign</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="submissions" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative" data-testid="tab-submissions">
              <Inbox className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Submissions</span>
              <span className="sm:hidden">Subs</span>
              {questionSubmissions.filter(s => s.submission_status === "pending").length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-primary text-primary-foreground text-[10px] sm:text-xs rounded-full flex items-center justify-center">
                  {questionSubmissions.filter(s => s.submission_status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="schools" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative" data-testid="tab-schools">
              <School className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Schools</span>
              <span className="sm:hidden">Sch</span>
              {schoolApplications.filter(s => s.status === "pending").length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-amber-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center">
                  {schoolApplications.filter(s => s.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            {isMasterAdmin && (
              <TabsTrigger value="users" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-users">
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                Users
              </TabsTrigger>
            )}
            {isMasterAdmin && (
              <TabsTrigger value="referrals" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-referrals">
                <Gift className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Referrals</span>
                <span className="sm:hidden">Refs</span>
              </TabsTrigger>
            )}
            {isMasterAdmin && (
              <>
                <TabsTrigger value="nicknames" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-nicknames">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Nicknames</span>
                  <span className="sm:hidden">Nick</span>
                </TabsTrigger>
                <TabsTrigger value="tools" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-tools">
                  <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Tools</span>
                  <span className="sm:hidden">Tools</span>
                </TabsTrigger>
                <TabsTrigger value="content" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="tab-content">
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Content</span>
                  <span className="sm:hidden">Text</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-6">
            <QuestionsTab
              questions={questions}
              isMasterAdmin={isMasterAdmin}
              track={questionsTrack}
              onTrackChange={setQuestionsTrack}
              onAddQuestion={openNewQuestion}
              onEditQuestion={openEditQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onDuplicateQuestion={handleDuplicateQuestion}
              onMakeBlocksVersion={handleMakeBlocksVersion}
              onGenerateBlocks={() => setShowGenerateBlocks(true)}
              onConvertSolution={handleConvertSolution}
              onConvertMissing={(targets) => convertSolutions(targets)}
              onBuildSolution={(q) => navigate(`/admin/blocks-solution/${q.question_id}`)}
              onGenerateVariations={(q) => setVariationsFor(q)}
              converting={convertingSolutions}
              onRecheckSolution={handleRecheckSolution}
              rechecking={recheckingSolutions}
              onBulkAction={handleBulkQuestions}
            />
          </TabsContent>

          {/* Drills Tab (Admin or Master Admin) */}
          {isAdminOrMaster && (
            <TabsContent value="drills" className="space-y-6">
              <DrillsTab isMasterAdmin={isMasterAdmin} />
            </TabsContent>
          )}

          {/* Campaign order (Admin or Master Admin) */}
          {isAdminOrMaster && (
            <TabsContent value="order" className="space-y-6">
              <QuestionOrderTab isMasterAdmin={isMasterAdmin} onEditQuestion={(questionId) => {
                const q = questions.find((x) => x.question_id === questionId);
                if (q) openEditQuestion(q); else toast.error("Question not found - it may have been deleted");
              }} />
            </TabsContent>
          )}

          {/* Question Submissions Tab */}
          <TabsContent value="submissions" className="space-y-6">
            <SubmissionsTab
              submissions={questionSubmissions}
              onOpenSubmissionEditor={openSubmissionEditor}
            />
          </TabsContent>

          {/* Schools Tab */}
          <TabsContent value="schools" className="space-y-6">
            <SchoolsTab
              applications={schoolApplications}
              onApproveSchool={handleApproveSchool}
              onRejectSchool={handleRejectSchool}
              isMasterAdmin={isMasterAdmin}
            />
          </TabsContent>

          {/* Users Tab (Master Admin Only) */}
          {isMasterAdmin && (
            <TabsContent value="users" className="space-y-6">
              <UserManagementTab />
            </TabsContent>
          )}

          {/* Referrals Tab (Master Admin Only) */}
          {isMasterAdmin && (
            <TabsContent value="referrals" className="space-y-6">
              <ReferralsTab />
            </TabsContent>
          )}

          {/* Nicknames Tab - Master Admin Only */}
          {isMasterAdmin && (
            <TabsContent value="nicknames" className="space-y-6">
              <NicknamesTab 
                nicknameLists={nicknameLists}
                onRefresh={fetchNicknameLists}
              />
            </TabsContent>
          )}

          {/* Content Tab - Master Admin Only: intro mode picker, mode cards and plan copy */}
          {isMasterAdmin && (
            <TabsContent value="content" className="space-y-6">
              <ContentTab />
            </TabsContent>
          )}

          {/* Tools Tab - Master Admin Only */}
          {isMasterAdmin && (
            <TabsContent value="tools" className="space-y-6">
              <div className="bg-card rounded-2xl border border-border/40 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-xl bg-primary/10">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-lg">Admin Tools</h2>
                    <p className="text-sm text-muted-foreground">
                      System utilities and content generation
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Generate Blocks versions */}
                  <div className="border border-border rounded-xl p-4" data-testid="generate-blocks-card">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
                          <Blocks className="w-4 h-4 text-sky-500" />
                          Generate Blocks Versions
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Bulk-copy suitable Python questions (short solutions, simple inputs) into the Blocks track. Inputs are detected from the sample solution and can be adjusted before creating.
                        </p>
                      </div>
                      <Button onClick={() => setShowGenerateBlocks(true)} variant="outline" className="gap-2 border-sky-400 text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/40" data-testid="open-generate-blocks-btn">
                        <Blocks className="w-4 h-4" />Open Generator
                      </Button>
                    </div>
                  </div>

                  {/* Sample solution check */}
                  <SolutionCheckTool
                    onFinished={fetchData}
                    onEditQuestion={(questionId) => {
                      const q = questions.find((x) => x.question_id === questionId);
                      if (q) openEditQuestion(q); else toast.error("Question not found - it may have been deleted");
                    }}
                  />

                  {/* Drills pointing at deleted questions */}
                  <DrillRepairTool isMasterAdmin={isMasterAdmin} />

                  {/* Junior maps short of hidden gems */}
                  <GemFillTool isMasterAdmin={isMasterAdmin} onFilled={fetchData} />

                  {/* Stripe webhook deliveries (master admin) */}
                  {isMasterAdmin && <StripeWebhookHealth />}

                  {/* Profanity Scan */}
                  <div className="border border-border rounded-xl p-4" data-testid="profanity-scan-card">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
                          <Ban className="w-4 h-4 text-red-500" />
                          Profanity Scan
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Scan every question, drill, school name, nickname and sample answer for offensive words so nothing inappropriate is shown anywhere on the site.
                        </p>
                      </div>
                      <Button
                        onClick={handleRunProfanityScan}
                        disabled={scanningProfanity}
                        variant="outline"
                        className="gap-2 border-red-400 text-red-600 hover:bg-red-50"
                        data-testid="run-profanity-scan-btn"
                      >
                        {scanningProfanity ? (
                          <><Loader2 className="w-4 h-4 animate-spin" />Scanning...</>
                        ) : (
                          <><Ban className="w-4 h-4" />Run Profanity Scan</>
                        )}
                      </Button>
                    </div>

                    {scanningProfanity && scanPhase && (
                      <div className="mt-3 text-sm text-muted-foreground flex items-center gap-2" data-testid="profanity-scan-progress">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Scanning {scanPhase.phase || "content"}...
                        {scanPhase.scanned && Object.keys(scanPhase.scanned).length > 0 && (
                          <span>({Object.entries(scanPhase.scanned).map(([k, v]) => `${k}: ${v}`).join(", ")})</span>
                        )}
                      </div>
                    )}

                    {profanityResult && (
                      <div className="mt-4" data-testid="profanity-scan-results">
                        {profanityResult.total_flagged === 0 ? (
                          <div className="border rounded-lg p-3 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            All clear! No offensive words found across {profanityResult.scanned?.questions || 0} questions, {profanityResult.scanned?.drills || 0} drills, {profanityResult.scanned?.schools || 0} schools and {profanityResult.scanned?.users || 0} users.
                          </div>
                        ) : (
                          <div className="border rounded-lg p-3 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 space-y-3">
                            <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                              {profanityResult.total_flagged} item(s) flagged:
                            </p>
                            {Object.entries(profanityResult.results).map(([category, items]) => (
                              items.length > 0 && (
                                <div key={category} data-testid={`profanity-category-${category}`}>
                                  <p className="text-xs font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 mb-1">
                                    {category} ({items.length})
                                  </p>
                                  <ul className="space-y-1">
                                    {items.map((item) => (
                                      <li key={`${category}-${item.id}`} className="text-xs text-foreground bg-card rounded px-2 py-1 flex items-center justify-between gap-2 flex-wrap">
                                        <span className="font-medium truncate">
                                          {item.label}
                                          {item.email && <span className="text-muted-foreground"> ({item.email})</span>}
                                          {item.status && <span className="text-muted-foreground"> — {item.status}</span>}
                                        </span>
                                        <span className="shrink-0 flex gap-1 flex-wrap">
                                          {item.flagged_words.map((w, wi) => (
                                            <Badge key={wi} variant="destructive" className="text-xs font-mono">{w}</Badge>
                                          ))}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Editable Profanity Word Lists */}
                  <div className="border border-border rounded-xl p-4" data-testid="profanity-words-card">
                    <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
                      <Ban className="w-4 h-4 text-amber-500" />
                      Profanity Word Lists
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Edit the words used by every profanity check on the site (question &amp; drill creation, submissions, live drills, names and the scan). Separate words with commas or spaces.
                    </p>
                    {loadingProfanityWords ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading word lists...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block">
                            Blocked words <span className="text-muted-foreground font-normal">(these are always flagged)</span>
                          </label>
                          <Textarea
                            value={profanityWords.custom_words}
                            onChange={(e) => setProfanityWords((p) => ({ ...p, custom_words: e.target.value }))}
                            className="font-mono text-sm h-28"
                            placeholder="e.g. butt, boobs, wtf"
                            data-testid="profanity-custom-words-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">
                            Allowed words <span className="text-muted-foreground font-normal">(never flagged — e.g. programming words like len, strip)</span>
                          </label>
                          <Textarea
                            value={profanityWords.allowlist}
                            onChange={(e) => setProfanityWords((p) => ({ ...p, allowlist: e.target.value }))}
                            className="font-mono text-sm h-28"
                            placeholder="e.g. len, strip, slave"
                            data-testid="profanity-allowlist-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveProfanityWords}
                            disabled={savingProfanityWords}
                            className="gap-2"
                            data-testid="save-profanity-words-btn"
                          >
                            {savingProfanityWords ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Save Word Lists
                          </Button>
                          <Button
                            onClick={fetchProfanityWords}
                            disabled={savingProfanityWords}
                            variant="outline"
                            className="gap-2"
                            data-testid="reload-profanity-words-btn"
                          >
                            <RefreshCw className="w-4 h-4" /> Reload
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI Sample Code Generator */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">AI Sample Code Generator</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Generate working Python solutions for questions that don't have sample code. Each solution is validated against test cases before saving.
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                          <li>• Uses AI to generate Python code based on question description</li>
                          <li>• Validates code against all test cases before saving</li>
                          <li>• Only processes questions with test cases defined</li>
                          <li>• Skips questions that already have sample code</li>
                        </ul>
                        
                        {sampleCodeResult && (
                          <div className={`border rounded-lg p-3 mb-4 ${
                            sampleCodeResult.failed > 0 
                              ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800" 
                              : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                          }`}>
                            <p className={`text-sm ${sampleCodeResult.failed > 0 ? "text-amber-700 dark:text-amber-400" : "text-green-700 dark:text-green-400"}`}>
                              <strong>Generated:</strong> {sampleCodeResult.generated} sample codes<br />
                              <strong>Failed:</strong> {sampleCodeResult.failed} (validation failed)<br />
                              <strong>Remaining:</strong> {sampleCodeResult.total_remaining} questions still need code
                            </p>
                            {sampleCodeResult.errors && sampleCodeResult.errors.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-700">
                                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">Failed Questions:</p>
                                <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1">
                                  {sampleCodeResult.errors.map((err) => (
                                    <li key={err} className="break-words">• {err}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {sampleCodeResult.total_remaining > 0 && (
                              <p className="text-xs text-green-600 dark:text-green-500 mt-2">
                                Click again to process more questions (3 at a time)
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={handleGenerateSampleCodes}
                        disabled={generatingSampleCodes}
                        variant="outline"
                        className="gap-2 border-green-500 text-green-600 hover:bg-green-50"
                        data-testid="generate-sample-codes-btn"
                      >
                        {generatingSampleCodes ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Code className="w-4 h-4" />
                            Generate Sample Codes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* AI Hints Generator */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">AI Hints Generator</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Generate helpful hints for questions that don&apos;t have hints or use the default W3Schools hint.
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                          <li>• Uses AI to create educational hints based on question content</li>
                          <li>• Hints mention useful Python concepts without solving the problem</li>
                          <li>• Replaces default &quot;Learn more at W3Schools&quot; hints with custom ones</li>
                        </ul>
                        
                        {hintsResult && (
                          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 mb-4">
                            <p className="text-sm text-purple-700 dark:text-purple-400">
                              <strong>Generated:</strong> {hintsResult.generated} hints<br />
                              <strong>Failed:</strong> {hintsResult.failed}<br />
                              <strong>Remaining:</strong> {hintsResult.total_remaining} questions still need hints
                            </p>
                            {hintsResult.total_remaining > 0 && (
                              <p className="text-xs text-purple-600 dark:text-purple-500 mt-2">
                                Click again to process more questions (5 at a time)
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={handleGenerateHints}
                        disabled={generatingHints}
                        variant="outline"
                        className="gap-2 border-purple-500 text-purple-600 hover:bg-purple-50"
                        data-testid="generate-hints-btn"
                      >
                        {generatingHints ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Lightbulb className="w-4 h-4" />
                            Generate Hints
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Test Users Generator */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">Create Test Users</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Generate 10 test users with randomized scores for testing leaderboards and user features.
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                          <li>• Creates 10 users with unique nicknames</li>
                          <li>• You choose the leaderboards (Junior Blocks, Blocks, Python) and the points range - scores are always multiples of 10</li>
                          <li>• Test accounts use @test.codingdrills.au emails</li>
                          <li>• Users appear on the chosen leaderboards immediately</li>
                        </ul>
                        
                        {testUsersResult && (
                          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 mb-4">
                            <p className="text-sm text-orange-700 dark:text-orange-400">
                              <strong>Created:</strong> {testUsersResult.total_created} test users
                            </p>
                            <div className="mt-2 max-h-32 overflow-y-auto">
                              <ul className="text-xs text-orange-600 dark:text-orange-500 space-y-1">
                                {testUsersResult.users?.map((u, i) => (
                                  <li key={i} data-testid={`test-user-row-${i}`}>• {u.nickname}: {u.scores ? Object.entries(u.scores).map(([m, pts]) => `${MODE_META[m]?.label || m} ${pts} pts`).join(" · ") : `${u.score} pts`}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => setShowTestUsersConfirm(true)}
                        disabled={creatingTestUsers}
                        variant="outline"
                        className="gap-2 border-orange-500 text-orange-600 hover:bg-orange-50"
                        data-testid="create-test-users-btn"
                      >
                        {creatingTestUsers ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Users className="w-4 h-4" />
                            Create Test Users
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Legacy Question Creator Update */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">Update Legacy Question Creators</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Assign "CodingDrills System" as the creator for any questions that don't have a creator assigned.
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                          <li>• Updates questions missing created_by_nickname</li>
                          <li>• Sets creator to "CodingDrills System"</li>
                          <li>• Safe to run multiple times (no duplicates)</li>
                          <li>• Run this after deploying to production</li>
                        </ul>
                        
                        {legacyCreatorsResult && (
                          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 mb-4">
                            <p className="text-sm text-purple-700 dark:text-purple-400">
                              <strong>Updated:</strong> {legacyCreatorsResult.updated_count} questions<br />
                              <strong>Total questions:</strong> {legacyCreatorsResult.total_questions}<br />
                              <strong>With creator:</strong> {legacyCreatorsResult.questions_with_creator}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={handleUpdateLegacyCreators}
                        disabled={updatingLegacyCreators}
                        variant="outline"
                        className="gap-2 border-purple-500 text-purple-600 hover:bg-purple-50"
                        data-testid="update-legacy-creators-btn"
                      >
                        {updatingLegacyCreators ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4" />
                            Update Creators
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Reset All Drills */}
                  <div className="border border-red-200 dark:border-red-800 rounded-xl p-4 bg-red-50/30 dark:bg-red-900/10">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1 text-red-600 dark:text-red-400">Reset All Drills</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Remove all drill assignments from classes and reset all student drill progress to zero.
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                          <li>• Unassigns drills from all classes</li>
                          <li>• Deletes all student drill progress records</li>
                          <li>• Students will no longer see "Complete Coding Drill" button</li>
                          <li>• This action cannot be undone</li>
                        </ul>
                        
                        {drillResetResult && (
                          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                            <p className="text-sm text-red-700 dark:text-red-400">
                              <strong>Assignments deleted:</strong> {drillResetResult.assignments_deleted}<br />
                              <strong>Progress records deleted:</strong> {drillResetResult.progress_deleted}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => setShowDrillResetConfirm(true)}
                        disabled={resettingDrills}
                        variant="outline"
                        className="gap-2 border-red-500 text-red-600 hover:bg-red-50"
                        data-testid="reset-drills-btn"
                      >
                        {resettingDrills ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Resetting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            Reset Drills
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Confirm Create Test Users Dialog */}
          <Dialog open={showTestUsersConfirm} onOpenChange={setShowTestUsersConfirm}>
            <DialogContent data-testid="test-users-dialog">
              <DialogHeader>
                <DialogTitle>Create Test Users</DialogTitle>
                <DialogDescription>
                  10 test users will be created and appear on the leaderboards you pick straight away. Each user gets a random score in every chosen leaderboard, always a multiple of 10.
                </DialogDescription>
              </DialogHeader>
              {(() => {
                const opts = testUsersOpts;
                const toggleMode = (m) => setTestUsersOpts((o) => ({ ...o, modes: o.modes.includes(m) ? o.modes.filter((x) => x !== m) : [...o.modes, m] }));
                const snap = (v, fallback) => { const n = parseInt(v, 10); return Number.isFinite(n) ? Math.max(10, Math.round(n / 10) * 10) : fallback; };
                const problems = [];
                if (!opts.modes.length) problems.push("Pick at least one leaderboard");
                if (opts.min_points > opts.max_points) problems.push("Minimum can't be above the maximum");
                return (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Add users to which leaderboards?</p>
                      <div className="flex flex-wrap gap-2" data-testid="test-users-modes">
                        {MODES.map((m) => {
                          const on = opts.modes.includes(m);
                          const Icon = MODE_META[m].icon;
                          return (
                            <button key={m} type="button" onClick={() => toggleMode(m)} aria-pressed={on}
                              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${on ? "bg-orange-600 text-white border-orange-600" : "bg-background text-muted-foreground border-border hover:text-foreground"}`}
                              data-testid={`test-users-mode-${m}`}>
                              <Icon className="w-4 h-4" /> {MODE_META[m].label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Random points range <span className="text-muted-foreground font-normal">(steps of 10)</span></p>
                      <div className="flex items-center gap-2">
                        <Input type="number" min={10} step={10} value={opts.min_points} className="w-28" aria-label="Minimum points"
                          onChange={(e) => setTestUsersOpts((o) => ({ ...o, min_points: parseInt(e.target.value, 10) || 0 }))}
                          onBlur={(e) => setTestUsersOpts((o) => ({ ...o, min_points: snap(e.target.value, 10) }))}
                          data-testid="test-users-min-input" />
                        <span className="text-sm text-muted-foreground">to</span>
                        <Input type="number" min={10} step={10} value={opts.max_points} className="w-28" aria-label="Maximum points"
                          onChange={(e) => setTestUsersOpts((o) => ({ ...o, max_points: parseInt(e.target.value, 10) || 0 }))}
                          onBlur={(e) => setTestUsersOpts((o) => ({ ...o, max_points: snap(e.target.value, 100) }))}
                          data-testid="test-users-max-input" />
                        <span className="text-sm text-muted-foreground">points</span>
                      </div>
                    </div>
                    {problems.length > 0 && <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="test-users-problems">{problems.join(" · ")}</p>}
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowTestUsersConfirm(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateTestUsers} disabled={problems.length > 0 || opts.min_points % 10 !== 0 || opts.max_points % 10 !== 0 || opts.min_points < 10} className="bg-orange-600 hover:bg-orange-700" data-testid="test-users-create-btn">
                        Create 10 Users
                      </Button>
                    </DialogFooter>
                  </div>
                );
              })()}
            </DialogContent>
          </Dialog>

          {/* Confirm Reset All Drills Dialog */}
          <Dialog open={showDrillResetConfirm} onOpenChange={setShowDrillResetConfirm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600">Reset All Drills</DialogTitle>
                <DialogDescription>
                  <span className="text-red-500 font-medium">Warning: This action cannot be undone!</span>
                  <br /><br />
                  This will:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Remove all drill assignments from all classes</li>
                    <li>Delete all student drill progress records</li>
                    <li>Students will need to start drills from scratch after reassignment</li>
                  </ul>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDrillResetConfirm(false)}>
                  Cancel
                </Button>
                <Button onClick={handleResetAllDrills} className="bg-red-600 hover:bg-red-700">
                  Reset All Drills
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Tabs>
      </div>

      {/* Question Edit Dialog */}
      <DeleteQuestionDialog question={deleteTarget} onOpenChange={setDeleteTarget} onConfirm={deleteQuestionNow} />
      <GenerateBlocksTool open={showGenerateBlocks} onOpenChange={setShowGenerateBlocks} onCreated={(res) => { localStorage.setItem("admin_questions_track", "blocks"); setQuestionsTrack("blocks"); convertSolutions(res.created || []); }} />


      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="question-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion?.question_id ? "Edit Question" : "New Question"}
            </DialogTitle>
          </DialogHeader>

          {editingQuestion && (
            <div className="space-y-4 min-w-0">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Title</label>
                <Input
                  value={editingQuestion.title}
                  onChange={(e) =>
                    setEditingQuestion((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Question title"
                  data-testid="question-title-input"
                />
              </div>

              {editingQuestion.mode !== "junior" && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Description</label>
                <Textarea
                  value={editingQuestion.description}
                  onChange={(e) =>
                    setEditingQuestion((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Describe the problem..."
                  className="min-h-[100px]"
                  data-testid="question-description-input"
                />
              </div>
              )}

              {editingQuestion.mode !== "junior" && (<>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Input Format</label>
                  <Textarea
                    value={editingQuestion.input_format}
                    onChange={(e) =>
                      setEditingQuestion((prev) => ({ ...prev, input_format: e.target.value }))
                    }
                    placeholder="Describe the input format..."
                    data-testid="question-input-format"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Output Format</label>
                  <Textarea
                    value={editingQuestion.output_format}
                    onChange={(e) =>
                      setEditingQuestion((prev) => ({ ...prev, output_format: e.target.value }))
                    }
                    placeholder="Describe the output format..."
                    data-testid="question-output-format"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Example Input</label>
                  <Textarea
                    value={editingQuestion.example_input}
                    onChange={(e) =>
                      setEditingQuestion((prev) => ({ ...prev, example_input: e.target.value }))
                    }
                    placeholder={"Enter each input on a new line:\nMo\nAli"}
                    className="font-mono"
                    data-testid="question-example-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Example Output</label>
                  <Textarea
                    value={editingQuestion.example_output}
                    onChange={(e) =>
                      setEditingQuestion((prev) => ({ ...prev, example_output: e.target.value }))
                    }
                    placeholder="Mo Ali"
                    className="font-mono"
                    data-testid="question-example-output"
                  />
                </div>
              </div>

              {editingQuestion.mode === "blocks" ? (
                <div className="space-y-3" data-testid="question-solution-section">
                  <label className="text-sm font-medium block">Solution <span className="text-muted-foreground text-xs">(built with blocks)</span></label>
                  <BlockSolutionPanel
                    question={editingQuestion}
                    busy={saving}
                    converting={convertingSolutions.has(editingQuestion.question_id)}
                    onConvert={handleConvertFromDialog}
                    onBuild={handleOpenBlocksEditor}
                    showPreview={showSolutionPreview}
                    onTogglePreview={() => setShowSolutionPreview((v) => !v)}
                  />
                  <details className="rounded-lg border border-border px-3 py-2" open={!!(editingQuestion.sample_solution || "").trim()} data-testid="question-python-sample-details">
                    <summary className="text-sm font-medium cursor-pointer select-none">Python sample <span className="text-muted-foreground text-xs font-normal">(optional - filled from the blocks if left empty)</span></summary>
                    <div className="pt-3">
                      <PythonSampleEditor
                        label="Python sample"
                        value={editingQuestion.sample_solution}
                        onChange={(value) => setEditingQuestion((prev) => ({ ...prev, sample_solution: value }))}
                        onTest={handleTestSolution}
                        validating={validating}
                        validationError={validationError}
                        height="160px"
                      />
                    </div>
                  </details>
                </div>
              ) : (
                <PythonSampleEditor
                  value={editingQuestion.sample_solution}
                  onChange={(value) => setEditingQuestion((prev) => ({ ...prev, sample_solution: value }))}
                  onTest={handleTestSolution}
                  validating={validating}
                  validationError={validationError}
                />
              )}
              </>)}

              {/* Hint Field - for Student Plan subscribers */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Hint (for subscribers)</label>
                <Textarea
                  placeholder="Optional hint to help students solve this question..."
                  value={editingQuestion.hint || ""}
                  onChange={(e) =>
                    setEditingQuestion((prev) => ({ ...prev, hint: e.target.value }))
                  }
                  className="min-h-[80px]"
                  data-testid="question-hint"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This hint will be available to Student Plan subscribers.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Pool</label>
                  <Select
                    value={editingQuestion.pool}
                    onValueChange={(value) =>
                      setEditingQuestion((prev) => ({ ...prev, pool: value }))
                    }
                  >
                    <SelectTrigger data-testid="question-pool-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily Pool</SelectItem>
                      <SelectItem value="challenge">Challenge Pool</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">
                    Annual Date <span className="text-muted-foreground text-xs">(optional)</span>
                  </label>
                  <MonthDayPicker
                    value={editingQuestion.date}
                    onChange={(date) => setEditingQuestion((prev) => ({ ...prev, date }))}
                    placeholder="No date set"
                  />
                </div>
              </div>

              {/* Track: Junior Blocks | Blocks | Python (+ input schema for Blocks, navigation grid for Junior) */}
              <div className="rounded-xl border border-border p-4 space-y-3" data-testid="question-track-section">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <label className="text-sm font-medium block">Track</label>
                    <p className="text-xs text-muted-foreground">{editingQuestion.mode === "junior" ? "Junior Blocks questions are drill navigation grids solved with a few blocks." : editingQuestion.mode === "blocks" ? "Blocks questions appear only in Blocks mode and are solved by dragging blocks." : "Python questions are typed in the code editor."}</p>
                  </div>
                  <div className="inline-flex items-center rounded-full border border-border bg-muted/40 p-0.5" role="radiogroup" data-testid="question-mode-toggle">
                    {MODES.map((v) => ({ v, label: MODE_META[v].label, Icon: MODE_META[v].icon })).map(({ v, label, Icon }) => (
                      <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={(editingQuestion.mode || "python") === v}
                        onClick={() => setEditingQuestion((prev) => ({ ...prev, mode: v, blocks: v === "junior" ? { ...(prev.blocks || {}), kind: "nav", inputs: [], nav: prev.blocks?.nav || emptyNav() } : v === "blocks" ? { ...(prev.blocks || {}), kind: "program", inputs: prev.blocks?.inputs || [] } : prev.blocks }))}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors ${(editingQuestion.mode || "python") === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid={`question-mode-${v}`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </button>
                    ))}
                  </div>
                </div>
                {editingQuestion.mode === "blocks" && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">Program inputs <span className="text-muted-foreground text-xs">(one per input line, in order)</span></label>
                      <BlocksInputsEditor
                        inputs={editingQuestion.blocks?.inputs || []}
                        onChange={(inputs) => setEditingQuestion((prev) => ({ ...prev, blocks: { ...(prev.blocks || {}), inputs } }))}
                        testId="question-blocks-inputs"
                      />
                    </div>
                    <BlockRulesEditor
                      categories={BLOCKS_CATEGORIES}
                      excludedCategories={editingQuestion.blocks?.excluded_categories || []}
                      excludedBlocks={editingQuestion.blocks?.excluded_blocks || []}
                      lockedCategories={["program"]}
                      onChange={(rules) => setEditingQuestion((prev) => ({ ...prev, blocks: { ...(prev.blocks || {}), ...rules } }))}
                      testId="question-blocks-rules"
                      hint="Hidden blocks disappear from the toolbox and are rejected on submit."
                    />
                  </div>
                )}
                {editingQuestion.mode === "junior" && (
                  <div className="space-y-4" data-testid="question-nav-section">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">Grid <span className="text-muted-foreground text-xs">{editingQuestion.pool === "challenge" ? "(pick a world, then place the vehicle, goals, obstacles and tunnel pairs)" : "(place the drill, treasure, rocks, danger zones and tunnel pairs - Ocean / Moon worlds are for Bonus challenges only)"}</span></label>
                      <NavGridEditor nav={editingQuestion.blocks?.nav} allowWorlds={editingQuestion.pool === "challenge"} onChange={(nav) => setEditingQuestion((prev) => ({ ...prev, blocks: { ...(prev.blocks || {}), nav } }))} />
                    </div>
                    <NavRulesEditor nav={editingQuestion.blocks?.nav} onChange={(nav) => setEditingQuestion((prev) => ({ ...prev, blocks: { ...(prev.blocks || {}), nav } }))} />
                    <p className="text-xs text-muted-foreground">Navigation questions are graded by simulating the drill - no test cases or Python solution needed. Use "Generate block solution" or "Build in blocks" below to record a program that collects every goal.</p>
                  </div>
                )}
                {editingQuestion.mode === "junior" && (
                  <BlockSolutionPanel
                    question={editingQuestion}
                    busy={saving}
                    generating={generatingNavSolution}
                    onGenerateNav={handleGenerateNavSolution}
                    onBuild={handleOpenBlocksEditor}
                    showPreview={showSolutionPreview}
                    onTogglePreview={() => setShowSolutionPreview((v) => !v)}
                  />
                )}
              </div>

              {/* Creator Email (Master Admin Only) */}
              {isMasterAdmin && (
                <div>
                  <label className="text-sm font-medium mb-1.5 block">
                    Created By <span className="text-muted-foreground text-xs">(user email)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="email"
                      placeholder="user@example.com"
                      value={editingQuestion.created_by_email || ""}
                      onChange={(e) =>
                        setEditingQuestion((prev) => ({ ...prev, created_by_email: e.target.value }))
                      }
                      className="flex-1"
                      data-testid="question-created-by-email"
                    />
                  </div>
                  {editingQuestion.created_by_nickname && (
                    <div className="mt-2 p-2 rounded-md bg-muted/50 text-sm">
                      <span className="text-muted-foreground">Nickname: </span>
                      <span className="font-medium text-foreground">{editingQuestion.created_by_nickname}</span>
                    </div>
                  )}
                  {editingQuestion.created_by_email && editingQuestion.created_by_nickname && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Email: {editingQuestion.created_by_email}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty for admin-created questions. Enter user email to credit them as creator.
                  </p>
                </div>
              )}

              {/* Test Cases */}
              {editingQuestion.mode !== "junior" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Test Cases</label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addTestCase}
                    className="gap-1"
                    data-testid="add-test-case-btn"
                  >
                    <Plus className="w-3 h-3" />
                    Add Test Case
                  </Button>
                </div>
                <div className="space-y-4">
                  {editingQuestion.test_cases?.map((tc, tcIndex) => (
                    <div key={tcIndex} className="p-4 border border-border rounded-xl bg-muted/30">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-muted-foreground">
                          Test Case {tcIndex + 1}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTestCase(tcIndex)}
                          className="h-7 px-2 text-destructive hover:text-destructive"
                          data-testid={`remove-test-case-${tcIndex}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                      
                      {/* Input Lines */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs text-muted-foreground">Input Lines</label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addInputLine(tcIndex)}
                            className="h-6 px-2 text-xs gap-1"
                            data-testid={`add-input-line-${tcIndex}`}
                          >
                            <Plus className="w-3 h-3" />
                            Add Input
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {(tc.inputs || ['']).map((inputVal, inputIndex) => (
                            <div key={inputIndex} className="flex gap-2 items-center">
                              <span className="text-xs text-muted-foreground w-6 text-right">
                                {inputIndex + 1}.
                              </span>
                              <Input
                                value={inputVal}
                                onChange={(e) => updateTestCaseInput(tcIndex, inputIndex, e.target.value)}
                                placeholder={`Input line ${inputIndex + 1}`}
                                className="font-mono text-sm flex-1"
                                data-testid={`test-case-${tcIndex}-input-${inputIndex}`}
                              />
                              {(tc.inputs || []).length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeInputLine(tcIndex, inputIndex)}
                                  className="h-8 w-8 shrink-0"
                                  data-testid={`remove-input-${tcIndex}-${inputIndex}`}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Expected Output */}
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1.5">Expected Output (multi-line)</label>
                        <textarea
                          value={tc.expected_output}
                          onChange={(e) => updateTestCase(tcIndex, "expected_output", e.target.value)}
                          placeholder={"Line 1\nLine 2"}
                          className="w-full min-h-[80px] p-2 rounded-md bg-background border border-input resize-y font-mono text-sm"
                          data-testid={`test-case-output-${tcIndex}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          )}

          <DialogFooter className="sm:justify-between gap-2">
            {editingQuestion?.question_id ? (
              <Button variant="outline" className="gap-2 border-primary/40 text-primary" disabled={saving} onClick={handleGenerateVariationsFromDialog} data-testid="question-generate-variations-btn">
                <Layers className="w-4 h-4" /> Generate 5 similar problems
              </Button>
            ) : <span />}
            <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveQuestion}
              disabled={saving}
              className="gap-2"
              data-testid="save-question-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VariationsDialog
        question={variationsFor}
        open={!!variationsFor}
        onClose={() => setVariationsFor(null)}
        onCreated={handleVariationsCreated}
      />

      {/* Submission Review/Edit Modal */}
      <Dialog open={!!editingSubmission} onOpenChange={(open) => { if (!open) { setEditingSubmission(null); setRejectingSubmission(false); setRejectReason(""); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Review & Edit Submission
            </DialogTitle>
          </DialogHeader>

          {editSubmissionData && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Title *</label>
                  <Input
                    value={editSubmissionData.title}
                    onChange={(e) => handleEditSubmissionChange("title", e.target.value)}
                    placeholder="Question title"
                    data-testid="edit-submission-title"
                  />
                </div>
                {editingSubmission?.mode !== "junior" && (
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Description *</label>
                  <Textarea
                    value={editSubmissionData.description}
                    onChange={(e) => handleEditSubmissionChange("description", e.target.value)}
                    placeholder="Question description"
                    rows={3}
                    data-testid="edit-submission-description"
                  />
                </div>
                )}
              </div>

              {/* Input/Output Format */}
              {editingSubmission?.mode !== "junior" && (<>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Input Format</label>
                  <Textarea
                    value={editSubmissionData.input_format}
                    onChange={(e) => handleEditSubmissionChange("input_format", e.target.value)}
                    placeholder="Describe input format"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Output Format</label>
                  <Textarea
                    value={editSubmissionData.output_format}
                    onChange={(e) => handleEditSubmissionChange("output_format", e.target.value)}
                    placeholder="Describe output format"
                    rows={2}
                  />
                </div>
              </div>

              {/* Example */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Example Input</label>
                  <Textarea
                    value={editSubmissionData.example_input}
                    onChange={(e) => handleEditSubmissionChange("example_input", e.target.value)}
                    placeholder="5&#10;3"
                    rows={2}
                    className="font-mono"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Example Output</label>
                  <Textarea
                    value={editSubmissionData.example_output}
                    onChange={(e) => handleEditSubmissionChange("example_output", e.target.value)}
                    placeholder="8"
                    rows={2}
                    className="font-mono"
                  />
                </div>
              </div>

              {/* Hint (Admin can add) */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Hint (Optional - Admin can add)</label>
                <Textarea
                  value={editSubmissionData.hint}
                  onChange={(e) => handleEditSubmissionChange("hint", e.target.value)}
                  placeholder="Add a hint for students..."
                  rows={2}
                />
              </div>

              {/* Sample Solution with CodeMirror */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium">Sample Solution *</label>
                  <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-600 font-medium">Python</span>
                </div>
                <div className="rounded-lg overflow-hidden border border-zinc-700">
                  <CodeMirror
                    value={editSubmissionData.sample_solution}
                    height="180px"
                    extensions={[python()]}
                    theme={oneDark}
                    onChange={(value) => handleEditSubmissionChange("sample_solution", value)}
                    className="text-sm"
                    data-testid="edit-submission-solution"
                  />
                </div>
              </div>

              {/* Test Cases */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium">Test Cases *</label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addEditTestCase}
                    className="gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add Test Case
                  </Button>
                </div>
                <div className="space-y-3 max-h-[200px] overflow-y-auto">
                  {editTestCases.map((tc, index) => (
                    <div key={index} className="p-3 border border-border rounded-lg bg-muted/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">Test Case {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditTestCase(index)}
                          className="h-6 px-2 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground">Input (multi-line)</label>
                          <Textarea
                            value={tc.input}
                            onChange={(e) => handleEditTestCaseChange(index, "input", e.target.value)}
                            placeholder={"5\n3"}
                            rows={4}
                            className="font-mono text-sm mt-1 resize-y"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Expected Output (multi-line)</label>
                          <Textarea
                            value={tc.expected_output}
                            onChange={(e) => handleEditTestCaseChange(index, "expected_output", e.target.value)}
                            placeholder={"Line 1\nLine 2\nLine 3"}
                            rows={4}
                            className="font-mono text-sm mt-1 resize-y"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </>)}
              {editingSubmission?.mode === "junior" && (
                <div className="space-y-2" data-testid="submission-nav-preview">
                  <label className="text-sm font-medium">Navigation grid</label>
                  <div className="max-w-sm"><NavGrid nav={editingSubmission.blocks.nav} /></div>
                  <p className="text-xs text-muted-foreground">Graded by simulation - the contributor's drill program was verified to reach the treasure{editingSubmission.blocks?.nav?.max_blocks ? ` (max ${editingSubmission.blocks.nav.max_blocks} blocks)` : ""}. Press Validate to re-run it.</p>
                  {editingSubmission.blocks?.solution_workspace && (
                    <div>
                      <label className="text-sm font-medium">Contributor's drill program</label>
                      <Suspense fallback={<div className="h-40 flex items-center justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>}>
                        <BlocksSolutionView workspace={editingSubmission.blocks.solution_workspace} inputs={[]} python={editingSubmission.blocks.solution_python || editingSubmission.sample_solution || ""} maxHeight={300} testId="submission-nav-solution-preview" />
                      </Suspense>
                    </div>
                  )}
                </div>
              )}

              {/* Validation Result */}
              {submissionValidationResult && (
                <div className={`p-4 rounded-lg border ${
                  submissionValidationResult.allPassed 
                    ? "bg-green-500/10 border-green-500/30" 
                    : "bg-red-500/10 border-red-500/30"
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {submissionValidationResult.allPassed ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-medium text-green-600">{submissionValidationResult.nav ? "The drill program reaches the treasure!" : "All test cases passed!"}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <span className="font-medium text-red-600">{submissionValidationResult.nav ? "The drill program doesn't reach the treasure" : "Some test cases failed"}</span>
                      </>
                    )}
                  </div>
                  <div className="space-y-2">
                    {submissionValidationResult.results.map((r, i) => (
                      <div key={i} className={`text-xs p-2 rounded ${r.passed ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <span className={r.passed ? "text-green-600" : "text-red-600"}>
                          Test {i + 1}: {r.passed ? "PASSED" : "FAILED"}
                        </span>
                        {!r.passed && (
                          <div className="mt-1 text-muted-foreground">
                            Expected: "{r.expected}" | Got: "{r.actual}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submitted by info */}
              <div className="text-sm text-muted-foreground border-t pt-4">
                <div className="flex flex-col gap-1">
                  <div>
                    <span className="text-xs">Nickname: </span>
                    <span className="font-medium text-foreground">{editingSubmission?.created_by_nickname || "Unknown"}</span>
                  </div>
                  {editingSubmission?.created_by_email && (
                    <div>
                      <span className="text-xs">Email: </span>
                      <span className="font-medium text-foreground">{editingSubmission.created_by_email}</span>
                    </div>
                  )}
                  {editingSubmission?.created_at && (
                    <div className="text-xs">
                      Submitted on {new Date(editingSubmission.created_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {rejectingSubmission && (
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/30 p-3 space-y-2" data-testid="reject-reason-panel">
              <label className="text-sm font-medium text-red-700 dark:text-red-300">Reason for the creator (optional)</label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value.slice(0, 300))}
                placeholder="e.g. The example output doesn't match the test cases - fix and resubmit"
                rows={2}
                className="bg-background"
                data-testid="reject-reason-input"
              />
              <p className="text-xs text-muted-foreground">Sent to the creator in-app and by email. The submission itself is deleted.</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => handleSuspendSubmitter(editingSubmission?.question_id, editingSubmission?.created_by_nickname)}
              disabled={!editingSubmission?.created_by_user_id}
              className="gap-1 text-amber-600 hover:bg-amber-50 border-amber-300 mr-auto"
              title={editingSubmission?.created_by_user_id ? "Suspend the submitter from submitting for 1 week" : "No linked user account for this submission"}
              data-testid="suspend-submitter-btn"
            >
              <Ban className="w-4 h-4" />
              Suspend 1 Week
            </Button>
            {rejectingSubmission ? (
              <>
                <Button variant="ghost" onClick={() => { setRejectingSubmission(false); setRejectReason(""); }} data-testid="reject-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleRejectSubmission(editingSubmission?.question_id)}
                  className="gap-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="reject-confirm-btn"
                >
                  <XCircle className="w-4 h-4" />
                  Confirm reject
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setRejectingSubmission(true)}
                className="gap-1 text-red-600 hover:bg-red-50"
                data-testid="reject-submission-btn"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </Button>
            )}
            <Button
              variant="outline"
              onClick={validateSubmissionSolution}
              disabled={validatingSubmission}
              className="gap-1"
              data-testid="validate-submission-btn"
            >
              {validatingSubmission ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Validate
                </>
              )}
            </Button>
            <Button
              onClick={saveAndApproveSubmission}
              disabled={savingSubmission || !submissionValidationResult?.allPassed}
              className="gap-1 bg-green-600 hover:bg-green-700"
              data-testid="approve-submission-btn"
            >
              {savingSubmission ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Save & Approve
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
